/* eslint react-hooks/exhaustive-deps: "warn" */
import { type ReactNode, useEffect, useRef } from 'react';

import cn from 'classnames';

import { Icon } from 'ui/atoms/Icon/Icon';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import { BrandLogo } from 'landing-pages/compare/BrandLogo';

import styles from './CompareTable.module.scss';

import type { CompareLogo, CompareTableRow } from '../../Compare.types';

type Props = {
  id?: string;
  title: ReactNode;
  description: ReactNode;
  rows: CompareTableRow[];
  competitorHeader: string;
  competitorLogo?: CompareLogo;
};

export const CompareTable = ({ id, title, description, rows, competitorHeader, competitorLogo }: Props) => {
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const revealables = rowRefs.current.filter((el): el is HTMLDivElement => Boolean(el));

    if (!revealables.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealed);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 },
    );

    revealables.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <Section id={id} className={styles.section}>
      <div className={styles.head}>
        <TitleBlock align="left" className={styles.titleBlock} title={title} description={description} />
      </div>

      <div className={styles.table}>
        <span className={styles.accent} aria-hidden="true" />

        <div className={styles.tableHead}>
          <span />
          <span className={styles.brandUs}>
            <BrandLogo className={styles.brandLogo} />
          </span>
          <span className={styles.brandThem}>
            {competitorLogo ? (
              <span className={styles.brandThemChip}>
                <Icon type={competitorLogo.type} className={styles.brandThemLogo} />
                <span className={styles.srOnly}>{competitorLogo.label}</span>
              </span>
            ) : (
              competitorHeader
            )}
          </span>
        </div>

        {rows.map((row, index) => (
          <div
            key={row.attribute}
            className={styles.row}
            style={{ transitionDelay: `${index * 60}ms` }}
            ref={(el) => {
              rowRefs.current[index] = el;
            }}
          >
            <span className={styles.attribute}>{row.attribute}</span>

            <span className={cn(styles.cell, styles.cellUs)}>
              <span className={styles.cellIcon} aria-hidden="true">
                ✓
              </span>
              <span className={styles.cellText}>{row.gd}</span>
            </span>

            <span className={cn(styles.cell, styles.cellThem)}>
              <span className={cn(styles.cellIcon, styles.cellIconThem)} aria-hidden="true" />
              <span className={styles.cellText}>{row.them}</span>
            </span>
          </div>
        ))}
      </div>
    </Section>
  );
};
